import { useTranslator } from '@bluebeam/react-i18n';
import Sort from 'components/Icons/Sort';
import { Button } from 'components/lib';
import OutsideClick from 'components/OutsideClick';
import React, { useState } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import withAppStore, { WithAppStoreProps } from 'utils/withAppStore';
import SortPopoverItem from './SortPopoverItem';

const modifiers = {
  offset: {
    offset: '0 15px',
  },
};

type SortDirection = 'ascending' | 'descending';

interface Props extends WithAppStoreProps {
  p?: boolean;
}

const SortPopover = (props: Props): React.ReactElement => {
  const { translate } = useTranslator(['mapView', 'buttons', 'filters']);
  const [open, setOpen] = useState(false);
  const { sortField: sortFieldProps, sortDirection: sortFieldDirection } =
    props.appState.rfiQuery;
  const [sortField, setSortField] = useState(sortFieldProps);
  const [sortDirection, setSortDirection] = useState(sortFieldDirection);

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleSortClick = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleUpdateSort = (newSortField: string): void => {
    setSortDirection((prevSortDirection) => {
      return newSortField === sortField && prevSortDirection === 'descending'
        ? 'ascending'
        : 'descending';
    });
    setSortField(newSortField);
  };

  const handleResetClick = (): void => {
    const sortFieldVariable = 'id';
    const sortDirectionVariable = 'descending';
    setOpen(false);
    setSortField(sortFieldVariable);
    setSortDirection(sortDirectionVariable);

    updateRFIQuery(sortFieldVariable, sortDirectionVariable);
  };

  const handleApplyClick = (): void => {
    updateRFIQuery(sortField, sortDirection);
    setOpen(false);
  };

  const updateRFIQuery = (
    sortField: string,
    sortDirection: SortDirection
  ): void => {
    const { appState } = props;
    const { rfiQuery } = appState;

    const updatedRFIQuery = rfiQuery
      .set('sortField', sortField)
      .set('sortDirection', sortDirection);

    props.actions.setRFIQuery(updatedRFIQuery);
  };

  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <div ref={ref} className="hs2 flex items-center">
            <Button
              id="rfi-sort-list"
              variant="tertiary"
              style={{ padding: '3px' }}
              onClick={handleSortClick}
              className="cursor-pointer"
            >
              <Sort width="24px" height="24px" />
            </Button>
          </div>
        )}
      </Reference>
      <Popper modifiers={modifiers}>
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              className="bgd-white-2 b-3"
              style={style}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div style={{ minWidth: '255px' }}>
                  <div>
                    <div
                      className="row items-center px4 bb-3"
                      style={{ height: '44px' }}
                    >
                      {translate('mapView:sortPopover.sortBy')}:
                    </div>
                    <SortPopoverItem
                      sortField={sortField}
                      sortDirection={sortDirection}
                      label={translate('mapView:sortPopover.id')}
                      id="number"
                      onUpdateSort={handleUpdateSort}
                    />
                    <SortPopoverItem
                      sortField={sortField}
                      sortDirection={sortDirection}
                      label={translate('mapView:sortPopover.subject')}
                      id="title"
                      onUpdateSort={handleUpdateSort}
                    />
                    <SortPopoverItem
                      sortField={sortField}
                      sortDirection={sortDirection}
                      label={translate('mapView:sortPopover.assignee')}
                      id="assignee"
                      onUpdateSort={handleUpdateSort}
                    />
                    <SortPopoverItem
                      sortField={sortField}
                      sortDirection={sortDirection}
                      label={translate('mapView:sortPopover.dueDate')}
                      id="duedate"
                      onUpdateSort={handleUpdateSort}
                    />
                  </div>
                  <div className="row justify-between p3">
                    <Button
                      variant="tertiary"
                      id="rfi-sort-list-reset"
                      onClick={handleResetClick}
                      className="mr2"
                    >
                      {translate('filters:reset')}
                    </Button>
                    <Button
                      id="rfi-sort-list-apply"
                      variant="primary"
                      onClick={handleApplyClick}
                    >
                      {translate('mapView:sortPopover.apply')}
                    </Button>
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default withAppStore<Props>(SortPopover);
